import { apiClient } from '../client';
import { API_ENDPOINTS } from '../config';
import { getOrganizationId } from '../organization';
import { getAiAnalyticsUrl } from '../aiAnalyticsUrl';

export interface AutomationFlow {
  id: string;
  name: string;
  description?: string;
  status: 'DRAFT' | 'ACTIVE' | 'PAUSED';
  businessAccountId: string;
  userId: string;
  triggerType?: string;
  triggerConfig?: Record<string, any>;
  flowData: Record<string, any>;
  createdAt: string;
  updatedAt: string;
  businessAccount?: {
    id: string;
    name: string;
    phoneNumber?: string;
  };
  user?: {
    id: string;
    name: string;
    email: string;
  };
  // Execution stats included in the response
  stats?: {
    totalRuns: number;
    successRate: number;
    lastRun: string | null;
  };
  totalRuns?: number;
  successRate?: number;
  lastRun?: string | null;
  /** Graph node count for list cards (null when unknown / not hydrated). */
  stepCount?: number | null;
  storageType?: string | null;
  storageLocation?: string | null;
}

export interface CreateAutomationFlowData {
  businessAccountId: string;
  name: string;
  description?: string;
  triggerType: string;
  triggerConfig: Record<string, any>;
  flowData: Record<string, any>;
}

export interface UpdateAutomationFlowData {
  name?: string;
  description?: string;
  status?: 'DRAFT' | 'ACTIVE' | 'PAUSED';
  triggerType?: string;
  triggerConfig?: Record<string, any>;
  flowData?: Record<string, any>;
  businessAccountId?: string;
}

export interface FlowExecution {
  id: string;
  flowId: string;
  status: 'PENDING' | 'RUNNING' | 'COMPLETED' | 'FAILED' | 'CANCELLED' | 'MANUALLY_PAUSED' | 'WAITING_FOR_RESPONSE';
  startedAt: string;
  completedAt?: string;
  errorMessage?: string;
  executionData: Record<string, any>;
  triggerData?: Record<string, any>;
  contactId: string;
  currentNodeId?: string;
  pausedAt?: string;
  manuallyPausedAt?: string;
  manuallyPausedBy?: string;
  createdAt: string;
  updatedAt: string;
  contact?: {
    id: string;
    name: string;
    phoneNumber: string;
  };
  flow?: {
    id: string;
    name: string;
  };
}


export const automationApi = {
  /**
   * Get all automation flows
   */
  async getAll(
    businessAccountId?: string,
    status?: string,
    search?: string,
    limit = 20,
    offset = 0,
    triggerType?: string,
  ) {
    const params: Record<string, any> = { limit, offset };
    if (businessAccountId) params.businessAccountId = businessAccountId;
    if (status) params.status = status;
    if (search) params.search = search;
    if (triggerType) params.triggerType = triggerType;
    return apiClient.get(API_ENDPOINTS.AUTOMATION_FLOWS, { params });
  },

  /**
   * Get automation flow by ID
   */
  /**
   * Fetch one flow.
   *
   * `fields: 'meta'` returns header metadata ONLY — no `flowData`. Use it for
   * any surface that renders a flow's identity (name / status / trigger /
   * created date) without editing the graph: the full response hydrates
   * `flow_data` from S3 for large flows, which is a wasted round-trip when
   * nothing reads the nodes. The builder and Export must keep using the
   * default (full) shape.
   */
  async getById(id: string, opts?: { fields?: 'meta' }) {
    const qs = opts?.fields === 'meta' ? '?fields=meta' : '';
    return apiClient.get(`${API_ENDPOINTS.AUTOMATION_FLOWS}/${id}${qs}`);
  },

  /**
   * Create automation flow
   */
  async create(data: CreateAutomationFlowData & { userId: string }) {
    const backendData = {
      name: data.name,
      description: data.description,
      status: 'DRAFT',
      triggerType: data.triggerType,
      triggerConfig: data.triggerConfig,
      flowData: data.flowData,
      isActive: false,
      businessAccountId: data.businessAccountId,
      userId: data.userId
    };
    return apiClient.post(API_ENDPOINTS.AUTOMATION_FLOWS, backendData);
  },

  /**
   * Update automation flow
   */
  async update(id: string, data: UpdateAutomationFlowData) {
    return apiClient.put(`${API_ENDPOINTS.AUTOMATION_FLOWS}/${id}`, data);
  },

  /**
   * Delete automation flow
   */
  async delete(id: string) {
    return apiClient.delete(`${API_ENDPOINTS.AUTOMATION_FLOWS}/${id}`);
  },

  /**
   * Execute automation flow
   */
  async execute(id: string, payload: { contactId: string; triggerData?: Record<string, any>; dryRun?: boolean }) {
    return apiClient.post(`${API_ENDPOINTS.AUTOMATION_FLOWS}/${id}/execute`, payload);
  },

  /**
   * Get flow executions
   */
  async getExecutions(flowId: string) {
    return apiClient.get(API_ENDPOINTS.FLOW_EXECUTIONS(flowId), {
      params: {
        orderBy: 'started_at',
        ascending: false
      }
    });
  },

  /**
   * Get execution summaries for multiple flows
   */
  async getExecutionSummaries(flowIds: string[]) {
    const promises = flowIds.map(flowId =>
      this.getExecutions(flowId).then(result => {
        const executions = result.data || [];

        // Sort executions by startedAt in descending order (most recent first)
        const sortedExecutions = executions.sort((a: FlowExecution, b: FlowExecution) =>
          new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime()
        );

        return {
          flowId,
          executions: sortedExecutions,
          lastRun: sortedExecutions[0]?.startedAt || null,
          totalRuns: executions.length,
          successRate: executions.length > 0
            ? (executions.filter((exec: FlowExecution) => exec.status === 'COMPLETED').length / executions.length) * 100
            : 0
        };
      }).catch(error => {
        return {
          flowId,
          executions: [],
          lastRun: null,
          totalRuns: 0,
          successRate: 0
        };
      })
    );
    return Promise.all(promises);
  },

  /**
   * Pause automation flow
   */
  async pause(id: string) {
    return apiClient.post(`${API_ENDPOINTS.AUTOMATION_FLOWS}/${id}/pause`, {});
  },

  /**
   * Resume automation flow
   */
  async resume(id: string) {
    return apiClient.post(`${API_ENDPOINTS.AUTOMATION_FLOWS}/${id}/resume`, {});
  },


  /**
   * Duplicate automation flow
   */
  async duplicate(id: string, data: { name: string; businessAccountId: string; userId: string }) {
    return apiClient.post(`${API_ENDPOINTS.AUTOMATION_FLOWS}/${id}/duplicate`, data);
  },

  /**
   * Publish automation flow (set status to active)
   */
  async publish(id: string) {
    return apiClient.put(`${API_ENDPOINTS.AUTOMATION_FLOWS}/${id}`, {
      status: 'ACTIVE',
      isActive: true
    });
  },

  /**
   * Generate automation flow via AI service
   */
  async generateAIFlow(data: { prompt: string; includeDescription?: boolean; businessAccountId?: string }) {
    const formData = new FormData();
    formData.append('query', data.prompt);
    formData.append('include_description', data.includeDescription === false ? 'false' : 'true');
    const organizationId = getOrganizationId();
    if (organizationId) {
      formData.append('organization_id', organizationId);
    }

    // Fetch AI Knowledge + brand website analysis and send as business_details (phone numbers stripped)
    try {
      const { organizationApi, brandWebsitesApi } = await import('./organization');
      const PHONE_RE = /(\+?\d[\d\s\-().]{7,}\d)/g;
      const stripPhones = (text: string) =>
        text.replace(PHONE_RE, (m: string) => {
          const digits = m.replace(/\D/g, '');
          return digits.length >= 7 ? '[phone hidden]' : m;
        });

      const parts: string[] = [];

      // 1. AI Knowledge context
      const ctxRes = await organizationApi.getAiContext();
      if (ctxRes.ok && ctxRes.data?.hasContent && ctxRes.data?.raw) {
        const sanitized = stripPhones(ctxRes.data.raw);
        const cleaned = sanitized.replace(/^Support phone:\s*\[phone hidden\]\s*$/m, '').trim();
        if (cleaned) parts.push(cleaned);
      }

      // 2. Brand website analysis data
      const webRes = await brandWebsitesApi.list();
      if (webRes.ok && webRes.websites?.length > 0) {
        for (const w of webRes.websites) {
          const lines: string[] = [];
          if (w.website_url) lines.push(`Website: ${w.website_url}`);
          if (w.site_name) lines.push(`Site name: ${w.site_name}`);
          if (w.description) lines.push(`Description: ${w.description}`);
          if (w.brand_tone) lines.push(`Brand tone: ${w.brand_tone}`);
          if (w.target_audience) lines.push(`Target audience: ${w.target_audience}`);
          if (w.design_style) lines.push(`Design style: ${w.design_style}`);
          if (w.suggested_tagline) lines.push(`Tagline: ${w.suggested_tagline}`);
          if (lines.length > 0) {
            parts.push(stripPhones(lines.join('\n')));
          }
        }
      }

      if (parts.length > 0) {
        // Cap business_details to avoid hitting the microservice's combined char limit
        const combined = parts.join('\n\n');
        formData.append('business_details', combined.slice(0, 2000));
      }
    } catch {
      // Non-fatal — proceed without business_details
    }

    const token = typeof window !== 'undefined' ? localStorage.getItem('auth_token') : null;

    const response = await fetch(getAiAnalyticsUrl('/automation-flow/generate'), {
      method: 'POST',
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      // The AI microservice (FastAPI) returns its reason in `detail`, e.g.
      // { "detail": "Query must be less than 2000 characters" }. Surface it so
      // the user sees the actual fix instead of a generic "HTTP 400".
      throw new Error(
        errorData.error || errorData.message || errorData.detail || `HTTP ${response.status}`,
      );
    }

    return response.json();
  },

  /**
   * Get all flow executions for a contact
   */
  async getContactFlowExecutions(contactId: string, status?: string) {
    const params: Record<string, any> = {};
    if (status) params.status = status;
    return apiClient.get(`/contacts/${contactId}/flow-executions`, { params });
  },

  /**
   * Get manually paused executions for a contact
   */
  async getContactPausedExecutions(contactId: string) {
    return apiClient.get(`/contacts/${contactId}/paused-executions`);
  },

  /**
   * Manually pause a flow execution
   */
  async pauseExecution(executionId: string, reason?: string) {
    const payload: Record<string, any> = {};
    if (reason) payload.reason = reason;
    return apiClient.post(`/flow-executions/${executionId}/manual-pause`, payload);
  },

  /**
   * Resume a manually paused execution
   */
  async resumeExecution(executionId: string) {
    return apiClient.post(`/flow-executions/${executionId}/manual-resume`, {});
  },

  /**
   * Get flow executions with filtering, search, and pagination
   */
  getFlowExecutionsWithFilters: async (flowId: string, params?: {
    status?: string;
    search?: string;
    limit?: number;
    pageToken?: string;
    orderBy?: string;
    ascending?: boolean;
  }) => {
    return apiClient.get(`/automation/flows/${flowId}/executions`, { params });
  },

  /**
   * Delete a flow execution
   */
  async deleteExecution(executionId: string) {
    return apiClient.delete(`/automation/executions/${executionId}`);
  },

  /**
   * Re-run a flow execution from a specific node, carrying over previous context
   */
  async retryExecution(executionId: string, startNodeId?: string) {
    return apiClient.post(`/automation/executions/${executionId}/retry`, {
      ...(startNodeId ? { startNodeId } : {}),
    });
  },

  /**
   * Get analytics for a flow (execution + message delivery stats)
   */
  async getFlowAnalytics(flowId: string) {
    return apiClient.get(`/automation/flows/${flowId}/analytics`);
  },

};
